import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateCard from './CandidateCardImproved';
import RecommendedCandidates from './RecommendedCandidates';
import MarketTrends from './MarketTrends';
import { isBelowCollegeBar } from './collegeGate';
// CheatingEvidence — rendered inline in the same content area when a
// recruiter clicks the "Suspicious / Cheating Detected" chip on a card.
// Same shell as the candidate list (sidebar + filter rail stay visible),
// matching how Reports.jsx is rendered embedded from this page.
import CheatingEvidence from '../CheatingEvidence/CheatingEvidence';
import EmployerProfilePopup from './EmployerProfilePopup';
import Pagination from '../MyJobs/Pagination';
import JobDescriptionView from '../MyJobs/JobDescriptionView';
import useCandidate from '../../Hooks/candidateHook';
import useProfileFreshness from '../../../Hooks/useProfileFreshness';
import axios from 'axios';
import { 
  ApplicationListSkeleton, 
  LoadingMoreCandidatesSkeleton,
  NoCandidatesSkeleton 
} from './SkeletonLoader';
import { getApiUrl } from '../../../config/api';
import { Container } from '@/Components/ui/container';
import { Stack } from '@/Components/ui/layout';
import { Inline } from '@/Components/ui/layout';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/Components/ui/select';
import { Badge } from '@/Components/ui/badge';
import { Heading, Text } from '@/Components/ui/typography';
import { Separator } from '@/Components/ui/separator';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/Components/ui/dialog';
import { Search, Filter, X, Upload, MapPin, Briefcase, Clock, CheckCircle2, XCircle, AlertCircle, Loader2, FileText, ChevronDown, CircleCheck, ChevronLeft, Users, LayoutGrid, List, FileUp, Settings, File, Folder, Sparkles, Coins, Copy, Check, Globe, Share2, MessageCircle, Send, Mail, Linkedin, Twitter, TrendingUp, Ban, Megaphone } from 'lucide-react';
import BroadcastDialog from '../Broadcast/BroadcastDialog';
import JobPeopleDialog from './JobPeopleDialog';
import { UsersRound } from 'lucide-react';
import { withSource } from '../Broadcast/jobShare';
import { Switch } from '@/Components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/Components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { getColor } from '@/config/theme';
import { reconcileCreditsFromCandidatesRaw } from '../../utils/creditsReconcile';
import CreditWidget from '@/HireWithUs/Components/creditWidget';
import creditServices from '@/Services/creditServices';
// AI Assistant — this page publishes its live state (candidates, filters,
// tab, selection, handlers) to the layout-level dock through
// useAssistantPage(); the dock itself is mounted once in EmployerLayout.
import { findCandidateByApplicationId, locateApplicationCard } from './copilot/CopilotContext';
import { useAssistantPage } from './copilot/useAssistantPage';
import { useCompanyAccess } from '@/HireWithUs/Context/CompanyAccessContext';

// Mirrors the backend slugify (Web-Interviewer/database/public_jobs_db.py)
// so the public careers-page link (/c/<slug>/<jobId>) resolves to the same
// company the backend's find_company_by_slug() matches. 'Acme Corp' →
// 'acme-corp'. Idempotent, so passing an already-slugified value is safe.
function slugifyCompany(text) {
  if (!text) return '';
  return String(text)
    .normalize('NFKD')
    // After NFKD, 'é' becomes 'e' + a combining mark; stripping all
    // non-ASCII removes the mark (and any other non-ASCII), matching
    // Python's unicodedata.normalize('NFKD').encode('ascii','ignore').
    .replace(/[^\x00-\x7F]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')       // any run of non-alphanumerics → single dash
    .replace(/^-+|-+$/g, '');          // trim leading/trailing dashes
}

// Share-message emojis built at RUNTIME via String.fromCodePoint instead
// of literal characters or \u{...} escapes — this project's bundler
// corrupts both (literal 4-byte emoji get mangled to U+FFFD on load, and
// \u{...} escapes get stripped to "{1F680}" in the served bundle). A
// number + function call has nothing for the bundler to mangle, and the
// correct emoji is constructed in memory at run time.
const SHARE_EMOJI = {
  wave: String.fromCodePoint(0x1f44b),
  rocket: String.fromCodePoint(0x1f680),
  office: String.fromCodePoint(0x1f3e2),
  pin: String.fromCodePoint(0x1f4cd),
  briefcase: String.fromCodePoint(0x1f4bc),
  calendar: String.fromCodePoint(0x1f5d3, 0xfe0f),
  point: String.fromCodePoint(0x1f449),
  mega: String.fromCodePoint(0x1f4e2),
  hands: String.fromCodePoint(0x1f64c),
  emDash: String.fromCodePoint(0x2014),
  midDot: String.fromCodePoint(0x00b7),
};

function ApplicationList({ jobId, onBack, highlightedApplicationId }) {
  const { can, scopedRolesEnabled } = useCompanyAccess();
  const [showPeople, setShowPeople] = useState(false);
  // States for pagination and profile selection
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const openedSearchCandidateRef = useRef(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [uploadFilesStatus, setUploadFilesStatus] = useState([]); // [{ name, progress, status, error }]
  const [overallProgress, setOverallProgress] = useState(0);
  const uploadXhrsRef = useRef({});
  const cancelledUploadsRef = useRef(new Set());
  const uploadIdsRef = useRef({});
  const [showLowScoringCandidates, setShowLowScoringCandidates] = useState(false);
  const [viewMode, setViewMode] = useState('list'); // 'list' (detailed) or 'grid' (compact cards)
  const [mainTab, setMainTab] = useState('applicants'); // 'applicants' | 'recommended'
  // Inline Cheating Evidence — when non-null, we render the
  // <CheatingEvidence embedded /> view INSTEAD of the candidate list in
  // the same content area. Pattern matches the inline-Reports view
  // used elsewhere in the platform: recruiter stays inside the page
  // shell (sidebar, filters, header), clicks Back, returns to the
  // candidate list without a full-page navigation. Stores the
  // coding_test_id of the card whose chip was clicked.
  const [inlineEvidenceTestId, setInlineEvidenceTestId] = useState(null);
  // Test type for the inline evidence dashboard: null → coding (all tabs); a
  // non-coding type (screening/pnb/communication/apt) hides the code-only tabs.
  const [inlineEvidenceType, setInlineEvidenceType] = useState(null);
  // Opener passed to cards. Coding chips call it with just an id (type stays
  // null → coding view); proctoring shields pass a type so the dashboard opens
  // in its dedicated non-coding view.
  const openInlineEvidence = (id, type = null) => {
    setInlineEvidenceTestId(id);
    setInlineEvidenceType(type);
  };
  const [isDragging, setIsDragging] = useState(false);
  const [viewingJobDescription, setViewingJobDescription] = useState(null);
  const [evaluating, setEvaluating] = useState({});
  const [balance, setBalance] = useState('-');
  const [costPerResume, setCostPerResume] = useState(null);
  // Public job link — when the recruiter has toggled this job public (the
  // "eye" on My Jobs / JobsTable, i.e. jd.is_public = true), we surface a
  // shareable public link + copy button in the header. `isJobPublic` is
  // fetched from the same visibility endpoint JobList uses so the two
  // stay in sync. `linkCopied` drives the brief "Copied" confirmation.
  const [isJobPublic, setIsJobPublic] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);
  // Owning company of this job — display name + slug, both from the
  // per-job public endpoint below (NOT the logged-in recruiter, and NOT
  // jobDetails.company which is actually the company EMAIL). companyName
  // is used in the share message templates; companySlug in the link.
  const [companyName, setCompanyName] = useState('');
  const [companySlug, setCompanySlug] = useState('');
  // Broadcast dialog (publish + list + share) for this job.
  const [showBroadcast, setShowBroadcast] = useState(false);
  const navigate = useNavigate();

  // The candidate-facing URL for this job — the company's branded careers
  // subdomain, e.g. https://searchjob.searchjob.ai/jobs/<jobId>, which
  // PublicEntry resolves to that company's page with the job dialog open.
  //
  //   • Production (real domain): prepend the company slug as a subdomain
  //     on the apex domain (last two labels), so it works whether the
  //     recruiter is on the apex or already on a subdomain.
  //   • Localhost / IP: browsers have no real subdomains there, so use the
  //     `?subdomain=<slug>` override that getSubdomainFromLocation honors.
  //   • No slug yet (or job not public): plain `/jobs/<jobId>` deep link.
  const publicJobUrl = useMemo(() => {
    if (typeof window === 'undefined' || !jobId) return '';
    const { protocol, hostname, origin } = window.location;

    if (!companySlug) return `${origin}/jobs/${jobId}`;

    const isLocal =
      hostname === 'localhost' ||
      hostname.endsWith('.localhost') ||
      /^\d+\.\d+\.\d+\.\d+$/.test(hostname);
    if (isLocal) {
      return `${origin}/jobs/${jobId}?subdomain=${companySlug}`;
    }

    // Apex = last two labels (e.g. searchjob.ai); replace/prepend the
    // company slug as the subdomain.
    const labels = hostname.split('.');
    const apex = labels.length > 2 ? labels.slice(-2).join('.') : hostname;
    return `${protocol}//${companySlug}.${apex}/jobs/${jobId}`;
  }, [jobId, companySlug]);

  useEffect(() => {
    if (!showUploadPopup) return;
    
    async function loadBalance() {
      try {
        const data = await creditServices.getBalance();
        setBalance(String(data?.balance ?? "-"));
      } catch (error) {
        console.warn("[ApplicationList] Failed to load credit balance:", error);
        setBalance("-");
      }
    }

    loadBalance();
  }, [showUploadPopup]);

  useEffect(() => {
    async function loadCost() {
      try {
        const data = await creditServices.getCost('RESUME_PARSED');
        setCostPerResume(data);
      } catch (error) {
        console.warn("[ApplicationList] Failed to load resume parsing cost:", error);
        setCostPerResume(null);
      }
    }

    loadCost();
  }, []);
  
  // Filter states (extended with extra fields used by applyFilters)
  const [filters, setFilters] = useState({
    skills: '',
    experienceMin: '',
    experienceMax: '',
    location: '',
    resumeScoreMin: '',
    resumeScoreMax: '',
    status: 'all',
    testCompleted: 'all',
    // new fields:
    skillQuery: '',
    sortingCriteria: 'Resume_Score',
    relevanceFilter: 'all', // 'all' | 'great' | 'good'
    scoreFilterType: '', // e.g. 'Resume_Score' or 'screening_overall_score'
    minScore: '',
    maxScore: '',
    interviewFilter: '', // '' | "1" | "0"
    // "Uploaded by" filter — slices the candidate list by which actor
    // submitted the resume, mirroring the buckets the
    // <UploadSourceBadge> chip already shows on every candidate card
    // inside CandidateCardImproved.jsx. Default 'all' = no filter, so
    // first paint is identical to the previous behaviour.
    //
    // Value space:
    //   'all'        → no uploader filter (default)
    //   'myself'     → uploaded_by_email matches the logged-in viewer
    //   'recruiter'  → uploaded_by_type === 'recruiter' AND not me
    //                  (mutually exclusive with 'myself')
    //   'company'    → uploaded_by_type IN ('company_admin','super_admin')
    //                  — bundled because viewers mostly care about
    //                    "me vs the company side" rather than admin
    //                    vs company specifically
    uploaderFilter: 'all',
    // College-tier filter — slices by the candidate's platform college tier
    // (backend services/college_tiers.py). 'all' = no filter (default).
    //   'all' | 'tier1' | 'tier2' | 'premium' (tier1+tier2) | 'none'
    collegeTierFilter: 'all',
    // Applied-date buckets use the existing application created_at field
    // already returned by /api/candidatelist. Client-side only.
    appliedDateFilter: 'all',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  // "Require premium" JDs hide candidates below the college bar by default;
  // this toggle reveals them (they're never removed or rejected).
  const [showBelowCollegeBar, setShowBelowCollegeBar] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  const candidatesPerPage = 10;

  // Hooks to fetch candidates and profile data
  const { candidates = [], loading, error, refetch, refreshCandidates } = useCandidate(jobId);
  // Keep the employer view aligned with Company Admin's Fresh Profiles
  // behaviour. Freshness is independent from application workflow status.
  const { isFreshProfile, markProfileOpened } = useProfileFreshness(
    candidates,
    `employer-job:${jobId || 'unknown'}`,
    { ready: !loading }
  );

  // A candidate selected from All Jobs search arrives with its application ID
  // in the URL. Open that exact profile once the job's candidate list loads;
  // the ref prevents a re-fetch from reopening the dialog after it is closed.
  useEffect(() => {
    if (!highlightedApplicationId || !candidates.length) return;

    const target = candidates.find((candidate) =>
      String(candidate.application_id || candidate.id) === String(highlightedApplicationId),
    );
    if (!target || openedSearchCandidateRef.current === String(highlightedApplicationId)) return;

    openedSearchCandidateRef.current = String(highlightedApplicationId);
    setSelectedCandidate({ ...target, job_id: jobId });
  }, [candidates, highlightedApplicationId, jobId]);
  const [aiProcessingEnabled, setAiProcessingEnabled] = useState(true);
  const shortlistedMeetingCandidates = useMemo(() => {
    const sourceCandidates = candidates || [];
    const shortlisted = sourceCandidates.filter(
      (item) => !!item?.isShortlisted && !item?.isRejected
    );
    if (shortlisted.length) {
      return shortlisted;
    }
    return sourceCandidates.filter((item) => !item?.isRejected);
  }, [candidates]);

  // helper: normalize recruiter_rank
  function normalizeRank(val) {
    if (val === null || val === undefined || val === "") return null;
    const n = Number(val);
    return Number.isFinite(n) ? Math.trunc(n) : null;
  }

  // helper: keep both start/end of long filenames visible
  function middleEllipsisFilename(name, maxLength = 48) {
    if (!name || typeof name !== 'string') return '';
    if (name.length <= maxLength) return name;

    const lastDot = name.lastIndexOf('.');
    const hasExt = lastDot > 0 && name.length - lastDot <= 10; // keep common extensions
    const ext = hasExt ? name.slice(lastDot) : '';
    const base = hasExt ? name.slice(0, lastDot) : name;

    const available = Math.max(8, maxLength - 1 - ext.length); // 1 for ellipsis char
    const head = Math.max(4, Math.ceil(available * 0.6));
    const tail = Math.max(4, Math.floor(available * 0.4));
    return `${base.slice(0, head)}…${base.slice(-tail)}${ext}`;
  }

  // applyFilters now lives here and reads from `candidates` + `filters`
  const applyFilters = (candidatesSource = null) => {
    let filtered = [];
    if (candidatesSource) {
      filtered = [...candidatesSource];
    } else {
      filtered = [...candidates];
    }

    // Apply general search (searchQuery) across name/email/skills/etc
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(candidate => {
        const searchableFields = [
          candidate.name || '',
          candidate.email || '',
          candidate.skills || '',
          candidate.experience || '',
          candidate.currentLocation || candidate.location || '',
          candidate.phone || ''
        ].join(' ').toLowerCase();
        return searchableFields.includes(q);
      });
    }

    // Apply skillQuery (separate skill search) — matches combined skills_matching/skills
    if ((filters.skillQuery || '').trim() !== '') {
      const q = filters.skillQuery.toLowerCase().trim();
      filtered = filtered.filter(candidate =>
        (((candidate.skills_matching || '') + ' ' + (candidate.skills || '')).toLowerCase().includes(q))
      );
    }

    // Apply Skills filter (free text - matches candidate.skills)
    if (filters.skills && (filters.skills.trim() !== '')) {
      const q = filters.skills.toLowerCase().trim();
      filtered = filtered.filter(candidate => (candidate.skills || '').toLowerCase().includes(q));
    }

    // Experience range — numeric years from existing candidatelist fields.
    const hasExpMin = filters.experienceMin !== '' && filters.experienceMin != null;
    const hasExpMax = filters.experienceMax !== '' && filters.experienceMax != null;
    if (hasExpMin || hasExpMax) {
      const minExp = hasExpMin ? Number(filters.experienceMin) : null;
      const maxExp = hasExpMax ? Number(filters.experienceMax) : null;
      filtered = filtered.filter((candidate) => {
        const raw = candidate.exp_yrs ?? candidate.exp_years ?? candidate.experience;
        if (raw === null || raw === undefined || raw === '') return false;
        let years = Number(raw);
        if (!Number.isFinite(years)) {
          const match = String(raw).match(/(\d+(?:\.\d+)?)/);
          years = match ? Number(match[1]) : NaN;
        }
        if (!Number.isFinite(years)) return false;
        if (Number.isFinite(minExp) && years < minExp) return false;
        if (Number.isFinite(maxExp) && years > maxExp) return false;
        return true;
      });
    }

    // Location filter — current location is `cur_loc` on the existing API.
    if (filters.location && filters.location.trim() !== '') {
      const q = filters.location.toLowerCase().trim();
      filtered = filtered.filter((candidate) => {
        const loc = [
          candidate.currentLocation,
          candidate.location,
          candidate.cur_loc,
          candidate.preferred_location,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return loc.includes(q);
      });
    }

    // Resume score min/max (these are different from scoreFilterType min/max)
    if (filters.resumeScoreMin && filters.resumeScoreMin !== '') {
      const min = Number(filters.resumeScoreMin);
      filtered = filtered.filter(candidate => Number(candidate.Resume_Score || 0) >= min);
    }
    if (filters.resumeScoreMax && filters.resumeScoreMax !== '') {
      const max = Number(filters.resumeScoreMax);
      filtered = filtered.filter(candidate => Number(candidate.Resume_Score || 0) <= max);
    }

    // Status filter
    if (filters.status && filters.status !== 'all') {
      const sf = (filters.status || '').trim().toLowerCase();
      if (sf === 'rejected') {
        filtered = filtered.filter(candidate => {
          const s = (candidate.status || '').toLowerCase();
          if (candidate.isRejected) return true;
          return s === 'fail' || s === 'client rejected' || s.includes('rejected');
        });
      } else if (sf === 'shortlisted') {
        filtered = filtered.filter(c => !!c.isShortlisted);
      } else if (sf === 'interview') {
        filtered = filtered.filter(c => !!c.isInterviewScheduled);
      } else if (sf === 'new_profiles') {
        // A profile is new until a company user opens it. This intentionally
        // does not depend on the application's "applied" workflow status.
        filtered = filtered.filter(isFreshProfile);
      } else if (sf === 'viewed') {
        // Includes server-confirmed opened profiles and the immediate local
        // update made while the profile-view request is in flight.
        filtered = filtered.filter(candidate => !isFreshProfile(candidate));
      } else {
        // generic textual match on status
        filtered = filtered.filter(candidate => {
          const s = (candidate.status || '').toLowerCase();
          return s === sf;
        });
      }
    }

    // "Uploaded by" filter — see filters.uploaderFilter declaration above
    // for what each value means. Viewer email read from the same
    // sessionStorage / localStorage slots used elsewhere in this page
    // for admin identity (line ~601). Recruiter / employer logins also
    // populate one of these slots, so this works for both viewer types.
    // No-viewer fallback (empty string) makes 'myself' match nothing —
    // safer than matching everything when identity is unknown.
    if (filters.uploaderFilter && filters.uploaderFilter !== 'all') {
      const viewerEmail = (
        sessionStorage.getItem('admin_email') ||
        localStorage.getItem('admin_email') ||
        localStorage.getItem('employer_email') ||
        sessionStorage.getItem('employer_email') ||
        ''
      ).trim().toLowerCase();
      filtered = filtered.filter((candidate) => {
        const uploaderEmail = (candidate.uploaded_by_email || '').trim().toLowerCase();
        const type = candidate.uploaded_by_type || '';
        const isSelf = !!viewerEmail && !!uploaderEmail && uploaderEmail === viewerEmail;
        switch (filters.uploaderFilter) {
          case 'myself':
            return isSelf;
          case 'recruiter':
            // Other recruiters only — exclude the viewer's own rows
            // so they only appear under 'myself' (mutually exclusive).
            return type === 'recruiter' && !isSelf;
          case 'company':
            // Bundles company_admin + super_admin together. Public-page
            // submissions also land here because the backend stamps
            // them as super_admin (see public_jobs_routes.py).
            // Legacy `self_apply` rows are folded in too so older
            // applications surface alongside their new equivalents.
            return (
              type === 'company_admin' ||
              type === 'super_admin' ||
              type === 'self_apply'
            );
          default:
            return true;
        }
      });
    }

    // Interview scheduled filter (explicit radio: "1" yes, "0" no, '' all)
    if (filters.interviewFilter === '1') {
      filtered = filtered.filter(c => !!c.isInterviewScheduled);
    } else if (filters.interviewFilter === '0') {
      filtered = filtered.filter(c => !c.isInterviewScheduled);
    }

    // Test completion filter (completed/pending)
    if (filters.testCompleted && filters.testCompleted !== 'all') {
      if (filters.testCompleted === 'completed') {
        filtered = filtered.filter(candidate =>
          !!(candidate.screening_overall_score || candidate.personality_overall_score || candidate.behavioral_overall_score || candidate.coding_overall_score)
        );
      } else if (filters.testCompleted === 'pending') {
        filtered = filtered.filter(candidate =>
          !(candidate.screening_overall_score || candidate.personality_overall_score || candidate.behavioral_overall_score || candidate.coding_overall_score)
        );
      }
    }

    // Relevance filter (based on Resume_Score)
    if (filters.relevanceFilter === 'great') {
      filtered = filtered.filter(candidate => Number(candidate.Resume_Score || 0) >= 80);
    } else if (filters.relevanceFilter === 'good') {
      filtered = filtered.filter(candidate => {
        const sc = Number(candidate.Resume_Score || 0);
        return sc >= 50 && sc < 80;
      });
    }

    // College-tier filter — uses the backend-computed candidate.college_tier
    // (1 = Tier 1, 2 = Tier 2, null = unrecognised).
    if (filters.collegeTierFilter && filters.collegeTierFilter !== 'all') {
      filtered = filtered.filter(candidate => {
        const t = candidate.college_tier;
        switch (filters.collegeTierFilter) {
          case 'tier1':   return t === 1;
          case 'tier2':   return t === 2;
          case 'premium': return t === 1 || t === 2;
          case 'none':    return t !== 1 && t !== 2;
          default:        return true;
        }
      });
    }

    // Applied-date filter — uses created_at already on each candidate.
    if (filters.appliedDateFilter && filters.appliedDateFilter !== 'all' && filters.appliedDateFilter !== '') {
      const today = new Date();
      filtered = filtered.filter((candidate) => {
        const created = new Date(candidate.created_at);
        if (Number.isNaN(created.getTime())) return false;
        const diffDays = Math.ceil(Math.abs(today - created) / (1000 * 60 * 60 * 24));
        switch (filters.appliedDateFilter) {
          case 'today':
            return diffDays <= 1;
          case '7days':
            return diffDays <= 7;
          case '30days':
            return diffDays <= 30;
          default:
            return true;
        }
      });
    }

    // Score-type range filter (if scoreFilterType set, use minScore/maxScore)
    if (filters.scoreFilterType && filters.scoreFilterType !== '') {
      const min = Number(filters.minScore || 0);
      const max = Number(filters.maxScore || 100);
      filtered = filtered.filter(candidate => {
        const score = Number(candidate[filters.scoreFilterType] || 0);
        return score >= min && score <= max;
      });
    }

    // New Profiles are always shown newest first by the existing application
    // created_at timestamp, matching the Company Admin Fresh Profiles view.
    if (filters.status === 'new_profiles') {
      filtered = filtered.slice().sort((a, b) => {
        const ta = Date.parse(a.created_at || 0) || 0;
        const tb = Date.parse(b.created_at || 0) || 0;
        return tb - ta;
      });
    // Sorting
    } else if (filters.sortingCriteria) {
      const sortKey = filters.sortingCriteria;
      if (sortKey === 'recruiter_rank') {
        const arr = filtered.slice();
        arr.sort((a, b) => {
          const ra = normalizeRank(a.recruiter_rank);
          const rb = normalizeRank(b.recruiter_rank);
          const aKey = ra === null ? Infinity : ra;
          const bKey = rb === null ? Infinity : rb;
          if (aKey === bKey) {
            const scoreDiff = Number(b.Resume_Score || 0) - Number(a.Resume_Score || 0);
            if (scoreDiff !== 0) return scoreDiff;
            return (a.email || "").localeCompare(b.email || "");
          }
          return aKey - bKey;
        });
        filtered = arr;
      } else if (sortKey === 'rank') {
        const arr = filtered.slice();
        arr.sort((a, b) => {
          const ra = normalizeRank(a.rank);
          const rb = normalizeRank(b.rank);
          const aKey = ra === null ? Infinity : ra;
          const bKey = rb === null ? Infinity : rb;
          if (aKey === bKey) {
            const scoreDiff = Number(b.Resume_Score || 0) - Number(a.Resume_Score || 0);
            if (scoreDiff !== 0) return scoreDiff;
            return (a.email || "").localeCompare(b.email || "");
          }
          return aKey - bKey;
        });
        filtered = arr;
      } else if (sortKey === 'upload_date_desc' || sortKey === 'upload_date_asc') {
        const arr = filtered.slice();
        arr.sort((a, b) => {
          const ta = Date.parse(a.created_at || a.upload_date || a.uploaded_at || 0) || 0;
          const tb = Date.parse(b.created_at || b.upload_date || b.uploaded_at || 0) || 0;
          return sortKey === 'upload_date_desc' ? (tb - ta) : (ta - tb);
        });
        filtered = arr;
      } else {
        // numeric descending by default for score fields
        // "relevance" reuses Resume_Score sort (same field relevance filter uses)
        const effectiveSortKey = sortKey === 'relevance' ? 'Resume_Score' : sortKey;
        const arr = filtered.slice();
        arr.sort((a, b) => {
          // Handle both direct property and nested details property
          const getValue = (obj, key) => {
            // Try direct property first
            if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
              return Number(obj[key]) || 0;
            }
            // Try nested details property
            if (obj.details && obj.details[key] !== null && obj.details[key] !== undefined && obj.details[key] !== '') {
              return Number(obj.details[key]) || 0;
            }
            return 0;
          };
          
          const valA = getValue(a, effectiveSortKey);
          const valB = getValue(b, effectiveSortKey);
          
          // If values are equal, use email as tie-breaker for stable sort
          if (valB === valA) {
            return (a.email || "").localeCompare(b.email || "");
          }
          return valB - valA; // Descending order (higher scores first)
        });
        filtered = arr;
      }
    }

    setFilteredCandidates(filtered);

    // Reset to first page if current page became out of range
    const totalPagesNew = Math.max(1, Math.ceil(filtered.length / candidatesPerPage));
    if (currentPage > totalPagesNew) {
      setCurrentPage(1);
    }
  };

  // Run applyFilters when candidates, filters, searchQuery, or freshness changes.
  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, filters, searchQuery, isFreshProfile]);

  // Credits reconciliation: when candidates load here, consume credits for completed tests.
  useEffect(() => {
    const companyId =
      localStorage.getItem('companyAccountId') ||
      localStorage.getItem('companyId') ||
      localStorage.getItem('companyID') ||
      '';
    if (!companyId || !candidates || candidates.length === 0) return;

    try {
      reconcileCreditsFromCandidatesRaw({ companyId, candidates, fallbackJobId: jobId });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[credits] reconciliation failed on ApplicationList', e);
    }
  }, [candidates, jobId]);

  // Reset filters and search
  const resetFilters = () => {
    setFilters({
      skills: '',
      experienceMin: '',
      experienceMax: '',
      location: '',
      resumeScoreMin: '',
      resumeScoreMax: '',
      status: 'all',
      testCompleted: 'all',
      skillQuery: '',
      sortingCriteria: 'Resume_Score',
      relevanceFilter: 'all',
      scoreFilterType: '',
      minScore: '',
      maxScore: '',
      interviewFilter: '',
      uploaderFilter: 'all',  // reset Uploaded-by filter back to "all sources"
      collegeTierFilter: 'all',
      appliedDateFilter: 'all',
    });
    setSearchQuery('');
  };

  // Clear specific filter
  const clearFilter = (filterName) => {
    setFilters(prev => ({ ...prev, [filterName]: '' }));
  };

  // Fetch job details
  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const response = await axios.get(
          getApiUrl(`/api/job_description?jobId=${jobId}`),
          { withCredentials: true }  // ← ADD THIS
        );
        setJobDetails(response.data);
      } catch (error) {
        console.error('Error fetching job details:', error);
      }
    };

    if (jobId) {
      fetchJobDetails();
    }
  }, [jobId]);

  // Resolve this job's public link from the authoritative per-job source:
  // GET /api/public/jobs/<jobId>. It is keyed on the job_id, so both the
  // public status AND the company slug always belong to the company that
  // OWNS this job — never whoever happens to be logged in. The backend:
  //   • returns 200 only when the job is truly public (company master
  //     switch + per-job toggle + deadline not passed), else 404, and
  //   • returns `company` = the owning company's display name
  //     (COALESCE(employers.company_name, jd.company)).
  // We slugify that name the same way the backend does, so the resulting
  // /c/<slug>/<jobId> resolves to the correct company.
  useEffect(() => {
    if (!jobId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          getApiUrl(`/api/public/jobs/${encodeURIComponent(jobId)}`),
          { withCredentials: true }
        );
        if (cancelled) return;
        const ownerName = res.data?.company || '';
        setCompanyName(ownerName);
        setCompanySlug(slugifyCompany(ownerName));
        setIsJobPublic(true);
      } catch (err) {
        // 404 = job not publicly visible (so there's no valid public link
        // to share); any other error is treated the same — hide the link.
        if (!cancelled) {
          setIsJobPublic(false);
          setCompanyName('');
          setCompanySlug('');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [jobId]);

  // Copy arbitrary text to the clipboard, with a graceful fallback for
  // browsers/contexts where the async Clipboard API is unavailable
  // (e.g. non-HTTPS). Throws on failure so callers can toast an error.
  const copyText = async (text) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  };

  // Copy just the public link. Shows a toast + a brief inline "Copied" state.
  const handleCopyPublicLink = async () => {
    if (!publicJobUrl) return;
    try {
      await copyText(publicJobUrl);
      setLinkCopied(true);
      toast.success('Public job link copied to clipboard');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy link. Please copy it manually.');
    }
  };

  // ── Share message ─────────────────────────────────────────────────────
  // Friendly, emoji-rich template used for WhatsApp, Telegram, Email and
  // the "Copy message" action. Lines for missing JD fields are dropped so
  // it always reads cleanly. companyName is the display name — note
  // jobDetails.company is the company EMAIL, so it is never used here.
  // `withEmoji=false` produces a pure-ASCII version (used for WhatsApp,
  // where the wa.me hand-off was rendering emojis as broken characters).
  // `url` lets each platform embed its own source-tagged link (?src=…) so
  // applications can be attributed to the channel that produced them.
  const buildShareTemplate = (withEmoji = true, url = publicJobUrl) => {
    const E = SHARE_EMOJI;
    const p = (emoji) => (withEmoji ? `${emoji} ` : ''); // optional emoji prefix
    const dash = withEmoji ? E.emDash : '-';
    const lines = [
      withEmoji ? `Hi ${E.wave}` : 'Hi',
      '',
      "Hope you're doing well!",
      '',
      'We have an exciting opportunity that might interest you:',
      '',
      `${p(E.rocket)}We're Hiring: ${jobDetails?.job_title || 'this role'}`,
    ];
    if (companyName) lines.push(`${p(E.office)}Company: ${companyName}`);
    if (jobDetails?.location) lines.push(`${p(E.pin)}Location: ${jobDetails.location}`);
    if (jobDetails?.job_type) lines.push(`${p(E.briefcase)}Type: ${jobDetails.job_type}`);
    if (jobDetails?.experience_range) lines.push(`${p(E.calendar)}Experience: ${jobDetails.experience_range}`);
    lines.push(
      '',
      "If this sounds like a great fit for you, we'd love to hear from you!",
      '',
      `${p(E.point)}View the full job description & apply here:`,
      url,
      '',
      `${p(E.mega)}Know someone who'd be a great fit? Feel free to forward this to your network ${dash} referrals are always welcome!`,
      '',
      `Feel free to reach out if you have any questions. Good luck!${withEmoji ? ` ${E.hands}` : ''}`,
    );
    return lines.join('\n');
  };

  // X (Twitter) caps posts at 280 characters, so the full template won't
  // fit — a compact variant is used there instead.
  const buildTweet = (url = publicJobUrl) => {
    const E = SHARE_EMOJI;
    const company = companyName ? ` at ${companyName}` : '';
    const meta = [jobDetails?.location, jobDetails?.job_type].filter(Boolean).join(` ${E.midDot} `);
    return [
      `${E.rocket} We're Hiring: ${jobDetails?.job_title || 'this role'}${company}`,
      meta || null,
      `${E.point} Apply: ${url}`,
      '#hiring #jobs',
    ].filter((l) => l !== null).join('\n');
  };

  // LinkedIn post — copied to the clipboard (LinkedIn can't pre-fill text).
  const buildLinkedInPost = (url = publicJobUrl) => {
    const company = companyName ? ` at ${companyName}` : '';
    const details = [];
    if (jobDetails?.location) details.push(`Location: ${jobDetails.location}`);
    if (jobDetails?.job_type) details.push(`Employment Type: ${jobDetails.job_type}`);
    if (jobDetails?.experience_range) details.push(`Experience: ${jobDetails.experience_range}`);
    return [
      `We are hiring: ${jobDetails?.job_title || 'this role'}${company}.`,
      '',
      ...details,
      '',
      'If you or someone in your network is a good fit, view the full job description and apply here:',
      url,
      '',
      '#Hiring #JobOpening',
    ].join('\n');
  };

  // Open a platform share-intent URL in a new tab. noopener/noreferrer
  // for safety.
  const openShareWindow = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Share to a specific platform using its formal, tailored message.
  // Works the same on desktop and mobile — unlike the OS share sheet,
  // which on Windows only lists installed apps (Outlook, Copilot, …).
  //
  // LinkedIn is special: its share URL can't carry post text (LinkedIn
  // removed that capability), so we copy the formal post to the clipboard
  // and open the LinkedIn share box with the job-link preview attached —
  // the recruiter just pastes it (Ctrl/Cmd+V).
  const handleShareTo = async (platform) => {
    if (!publicJobUrl) return;
    const enc = encodeURIComponent;
    // Tag the link with the channel it is shared on (X is keyed "x").
    const taggedUrl = withSource(publicJobUrl, platform === 'twitter' ? 'x' : platform);

    switch (platform) {
      case 'whatsapp':
        // Emoji-free (pure ASCII) — WhatsApp's wa.me hand-off was rendering
        // the emojis as broken characters on this setup.
        openShareWindow(`https://wa.me/?text=${enc(buildShareTemplate(false, taggedUrl))}`);
        break;

      case 'telegram':
        // text = full template (link embedded); url = same link so Telegram
        // also renders a preview card for it.
        openShareWindow(`https://t.me/share/url?url=${enc(taggedUrl)}&text=${enc(buildShareTemplate(true, taggedUrl))}`);
        break;

      case 'twitter':
        openShareWindow(`https://twitter.com/intent/tweet?text=${enc(buildTweet(taggedUrl))}`);
        break;

      case 'email': {
        const subject = jobDetails?.job_title
          ? `Job Opening: ${jobDetails.job_title}${companyName ? ` at ${companyName}` : ''}`
          : 'Job Opening';
        window.location.href =
          `mailto:?subject=${enc(subject)}&body=${enc(buildShareTemplate(true, taggedUrl))}`;
        break;
      }

      case 'linkedin':
        try {
          await copyText(buildLinkedInPost(taggedUrl));
          toast.success('LinkedIn post copied — paste it into the box that opens (Ctrl/Cmd+V)');
        } catch (err) {
          toast('Opening LinkedIn — add your message there');
        }
        openShareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${enc(taggedUrl)}`);
        break;

      default:
        break;
    }
  };

  // Copy the full share-message template (with link) to paste anywhere.
  const handleCopyShareMessage = async () => {
    if (!publicJobUrl) return;
    try {
      await copyText(buildShareTemplate());
      setMessageCopied(true);
      toast.success('Share message copied — paste it anywhere');
      setTimeout(() => setMessageCopied(false), 2000);
    } catch (err) {
      toast.error('Could not copy message. Please copy it manually.');
    }
  };

  const uploadSingleFileXHR = (file, jobId, adminEmail, onProgress) => {
    return new Promise((resolve) => {
      const formData = new FormData();
      // backend loop expects file0, file1 ... sending file0 is fine for single-file requests
      formData.append('file', file);
      formData.append('job_id', jobId);

      const xhr = new XMLHttpRequest();
      const url = getApiUrl('/api/handle_resume_upload'); // keep same endpoint

      xhr.open('POST', url, true);
      xhr.withCredentials = true;
      if (adminEmail) xhr.setRequestHeader('X-Admin-Email', adminEmail);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && typeof onProgress === 'function') {
          onProgress(file.name, Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        // treat non-2xx as failure but still resolve to let aggregator continue
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const json = JSON.parse(xhr.responseText || '{}');
            resolve({ ok: true, fileName: file.name, body: json });
          } catch (e) {
            resolve({ ok: false, fileName: file.name, error: 'Invalid JSON from server' });
          }
        } else {
          // attempt to parse server error message
          let errMsg = `Upload failed (${xhr.status})`;
          try {
            const parsed = JSON.parse(xhr.responseText || '{}');
            errMsg = parsed.error || parsed.message || errMsg;
          } catch {}
          resolve({ ok: false, fileName: file.name, error: errMsg });
        }
      };

      xhr.onerror = () => {
        resolve({ ok: false, fileName: file.name, error: 'Network error' });
      };

      xhr.send(formData);
    });
  };

  // simple concurrency runner (keeps order)
  const runWithConcurrency = async (items, workerFn, concurrency = 4) => {
    const results = new Array(items.length);
    let idx = 0;
    const runners = new Array(concurrency).fill(null).map(async () => {
      while (true) {
        const i = idx++;
        if (i >= items.length) break;
        try {
          results[i] = await workerFn(items[i]);
        } catch (e) {
          results[i] = { ok: false, fileName: items[i].name, error: e?.message || e };
        }
      }
    });
    await Promise.all(runners);
    return results;
  };

  const MAX_RESUMES_PER_UPLOAD = 10;
  const ACTIVE_RESUME_UPLOAD_STATUSES = ['queued', 'uploading', 'processing', 'finalizing', 'preparing'];

  const makeResumeUploadId = () => {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch {}
    return `upl_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  };

  const stopResumeUpload = (fileIndex) => {
    cancelledUploadsRef.current.add(fileIndex);
    const uploadId = uploadIdsRef.current[fileIndex];
    const xhr = uploadXhrsRef.current[fileIndex];
    const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email') || '';

    if (uploadId) {
      fetch(getApiUrl('/api/cancel_resume_upload'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Email': adminEmail,
        },
        body: JSON.stringify({ upload_id: uploadId }),
        keepalive: true,
      }).catch(() => {});
    }

    if (xhr) {
      try { xhr.abort(); } catch {}
    }

    setUploadFilesStatus(prev => prev.map((item, idx) => {
      if (idx !== fileIndex) return item;
      if (!ACTIVE_RESUME_UPLOAD_STATUSES.includes(item.status)) return item;
      return { ...item, status: 'stopped', progress: 0, error: null };
    }));
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    let files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;

    // Validate file types
    const invalidFile = files.find(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return !['pdf', 'doc', 'docx', 'txt'].includes(ext);
    });
    
    if (invalidFile) {
      toast.error(`Unsupported file format: ${invalidFile.name}. Please upload PDF/DOC/DOCX/TXT only.`);
      return;
    }

    // Cap at max 10 resumes (same as file input)
    if (files.length > MAX_RESUMES_PER_UPLOAD) {
      toast.info(`Maximum ${MAX_RESUMES_PER_UPLOAD} resumes at a time. Only the first ${MAX_RESUMES_PER_UPLOAD} will be uploaded.`);
      files = files.slice(0, MAX_RESUMES_PER_UPLOAD);
    }

    // Process files as if they came from file input
    const syntheticEvent = { target: { files } };
    handleResumeUpload(syntheticEvent);
  };

  const handleResumeUpload = async (event) => {
    let files = Array.from(event.target.files || []);
    if (!files.length) return;

    const totalCost = aiProcessingEnabled && costPerResume != null
      ? costPerResume * files.length
      : 0;

    const hasInsufficientCredits = aiProcessingEnabled
      && balance != null
      && files.length > 0
      && totalCost > balance;

    if (hasInsufficientCredits) {
      toast.error("Insufficient credits", {
        description: `You need ${totalCost} credits to analyse ${files.length} resume${files.length !== 1 ? "s" : ""}, but only have ${balance}.`,
        action: {
          label: "Buy Credits",
          onClick: () => navigate("/hirewithus/billing"),
        },
        duration: 8000,
      });
      // Clear the file input so re-selecting the same files triggers onChange again
      event.target.value = "";
      return;
    }

    if (!jobId) {
      toast.error('Job ID is missing. Please try refreshing the page.');
      event.target.value = '';
      return;
    }

    const _companyId = localStorage.getItem('companyAccountId');
    if (!_companyId || _companyId === 'null' || _companyId === 'undefined') {
      toast.error('Session expired. Please log out and log in again.');
      event.target.value = '';
      return;
    }

    const invalidFile = files.find(f => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      return !['pdf', 'doc', 'docx', 'txt'].includes(ext);
    });
    if (invalidFile) {
      toast.error(`Unsupported file format: ${invalidFile.name}. Please upload PDF/DOC/DOCX/TXT only.`);
      event.target.value = '';
      return;
    }

    if (files.length > MAX_RESUMES_PER_UPLOAD) {
      toast.info(`Maximum ${MAX_RESUMES_PER_UPLOAD} resumes at a time. Only the first ${MAX_RESUMES_PER_UPLOAD} will be uploaded.`);
      files = files.slice(0, MAX_RESUMES_PER_UPLOAD);
    }

    const initStatus = files.map(f => ({
      name: f.name,
      progress: 0,
      status: 'queued',
      error: null,
      aiProcessed: aiProcessingEnabled,
    }));
    setUploadFilesStatus(initStatus);
    setIsUploading(true);
    setOverallProgress(0);
    uploadXhrsRef.current = {};
    cancelledUploadsRef.current = new Set();
    uploadIdsRef.current = {};

    const adminEmail = sessionStorage.getItem('admin_email') || localStorage.getItem('admin_email') || '';
    const url = getApiUrl('/api/handle_resume_upload');

    let successCount   = 0;
    let duplicateCount = 0;
    let failedCount    = 0;
    const allResults   = [];

    try {
      // Each file → independent parallel API request
      const uploadPromises = files.map((file, fileIndex) => {
        const uploadId = makeResumeUploadId();
        uploadIdsRef.current[fileIndex] = uploadId;

        // Mark as uploading immediately
        setUploadFilesStatus(prev => prev.map((item, idx) =>
          idx === fileIndex ? { ...item, status: 'uploading', progress: 0 } : item
        ));

        return uploadSingleFile(
          file,
          jobId,
          adminEmail,
          url,
          aiProcessingEnabled,
          (progress) => {
            if (cancelledUploadsRef.current.has(fileIndex)) return;
            setUploadFilesStatus(prev => {
              const next = prev.map((item, idx) =>
                idx === fileIndex && item.status !== 'stopped'
                  ? { ...item, progress: Math.round(progress * 0.7) }
                  : item
              );
              const avg = next.reduce((s, f) => s + f.progress, 0) / next.length;
              setOverallProgress(Math.round(avg));
              return next;
            });
          },
          {
            uploadId,
            onXhr: (xhr) => { uploadXhrsRef.current[fileIndex] = xhr; },
          }
        )
          .then((result) => {
            if (result?.error === 'upload_cancelled' || (
              cancelledUploadsRef.current.has(fileIndex) && !result?.new_candidates?.length
            )) {
              setUploadFilesStatus(prev => prev.map((item, idx) =>
                idx === fileIndex
                  ? { ...item, status: 'stopped', progress: 0, error: null }
                  : item
              ));
              return { file: file.name, result, success: false, duplicate: false, stopped: true };
            }

            // Upload done, now server-side processing
            setUploadFilesStatus(prev => prev.map((item, idx) =>
              idx === fileIndex && item.status !== 'stopped'
                ? { ...item, status: 'processing', progress: 80 }
                : item
            ));

            // Handle credit/plan errors returned by backend
            if (result.success === false && result.error) {
              let errorMsg = result.message || 'Upload failed';
              if (result.error === 'insufficient_credits') {
                errorMsg = `Insufficient credits (need ${result.required}, have ${result.balance})`;
                toast.error('Insufficient credits', {
                  description: errorMsg,
                  action: { label: 'Buy Credits', onClick: () => navigate('/hirewithus/billing') },
                  duration: 8000,
                });
              } else if (result.error === 'plan_expired') {
                toast.error('Plan expired', {
                  description: 'Your subscription has expired. Please renew to upload resumes.',
                  action: { label: 'Renew Plan', onClick: () => navigate('/hirewithus/billing') },
                  duration: 8000,
                });
              } else if (result.error === 'monthly_limit_reached') {
                // Custom monthly allowance used up (e.g. Balio). Message comes
                // from the server and already names the renewal date + support.
                const supportEmail = result.support_email || 'contact@searchjob.ai';
                toast.error('Monthly limit reached', {
                  id: 'monthly-limit-upload',   // one toast per batch, not one per file
                  description: errorMsg,
                  action: { label: 'Email support', onClick: () => { window.location.href = `mailto:${supportEmail}`; } },
                  duration: 12000,
                });
              } else {
                toast.error(`Upload failed: ${errorMsg}`, { duration: 6000 });
              }
              setUploadFilesStatus(prev => prev.map((item, idx) =>
                idx === fileIndex
                  ? { ...item, status: 'failed', progress: 0, error: errorMsg }
                  : item
              ));
              return { file: file.name, result, success: false, duplicate: false };
            }

            const isDuplicate = result.duplicates?.length > 0;
            const isSuccess   = result.new_candidates?.length > 0;

            if (isDuplicate) {
              const rawReason = result.duplicates[0]?.error || 'duplicate';
              // Backend duplicate codes → clear, human messages. Anything not
              // in this set that also doesn't read like a duplicate is a real
              // backend error surfaced through the duplicates channel.
              const DUP_MESSAGES = {
                duplicate: 'Duplicate profile found',
                email_match: 'Duplicate profile found — same email already applied',
                phone_name_match: 'Duplicate profile found — same phone & name already applied',
                experience_projects_match: 'Duplicate profile found — same experience & projects already applied',
              };
              const code = String(rawReason).trim().toLowerCase();
              const isKnownDuplicate =
                Object.prototype.hasOwnProperty.call(DUP_MESSAGES, code) ||
                code.includes('already') || code.includes('exist') || code.includes('duplicate');

              if (!isKnownDuplicate) {
                // A real backend error came through the duplicates channel.
                toast.error(`Upload failed: ${rawReason}`, { duration: 6000 });
                setUploadFilesStatus(prev => prev.map((item, idx) =>
                  idx === fileIndex
                    ? { ...item, status: 'failed', progress: 0, error: rawReason }
                    : item
                ));
                return { file: file.name, result, success: false, duplicate: false };
              }

              const friendly = DUP_MESSAGES[code] || 'Duplicate profile found';
              setUploadFilesStatus(prev => prev.map((item, idx) =>
                idx === fileIndex
                  ? { ...item, status: 'duplicate', progress: 100, error: friendly }
                  : item
              ));
              return { file: file.name, result, success: true, duplicate: true };
            }

            if (isSuccess) {
              setUploadFilesStatus(prev => prev.map((item, idx) =>
                idx === fileIndex
                  ? { ...item, status: 'success', progress: 100, error: null, aiProcessed: aiProcessingEnabled }
                  : item
              ));
              return { file: file.name, result, success: true, duplicate: false };
            }

            setUploadFilesStatus(prev => prev.map((item, idx) =>
              idx === fileIndex
                ? { ...item, status: 'failed', progress: 0, error: 'Processing failed — please try again' }
                : item
            ));
            return { file: file.name, result, success: false, duplicate: false };
          })
          .catch((err) => {
            if (cancelledUploadsRef.current.has(fileIndex)) {
              setUploadFilesStatus(prev => prev.map((item, idx) =>
                idx === fileIndex
                  ? { ...item, status: 'stopped', progress: 0, error: null }
                  : item
              ));
              return { file: file.name, error: err.message, success: false, duplicate: false, stopped: true };
            }

            const errorMessage = (err.message?.includes('timeout') || err.message?.includes('time'))
              ? 'Timeout — file too large or slow connection'
              : err.message || 'Upload failed';

            setUploadFilesStatus(prev => prev.map((item, idx) =>
              idx === fileIndex
                ? { ...item, status: 'failed', progress: 0, error: errorMessage }
                : item
            ));
            return { file: file.name, error: err.message, success: false, duplicate: false };
          });
      });

      const results = await Promise.all(uploadPromises);
      const stoppedCount = results.filter(r => r.stopped).length;

      results.forEach((r) => {
        allResults.push({ file: r.file, result: r.result, success: r.success, stopped: r.stopped });
        if (r.stopped) return;
        if (r.success) r.duplicate ? duplicateCount++ : successCount++;
        else failedCount++;
      });

      setOverallProgress(100);
      await new Promise(r => setTimeout(r, 500));

      if (successCount > 0) {
        try { refreshCandidates(); } catch {}
        window.dispatchEvent(new CustomEvent('refreshJobs'));
      }

      if (successCount === files.length) {
        setTimeout(() => {
          toast.success(`${successCount} resume${successCount > 1 ? 's' : ''} uploaded successfully!`);
          setShowUploadPopup(false);
          setUploadFilesStatus([]);
        }, 100);
      } else if (successCount > 0) {
        const failedFiles = allResults.filter(r => !r.success && !r.stopped).map(r => r.file);
        toast.warning(
          `Partial success — ✅ ${successCount} uploaded` +
          `${duplicateCount > 0 ? `, ⚠️ ${duplicateCount} duplicate(s)` : ''}` +
          `${stoppedCount > 0 ? `, ${stoppedCount} stopped` : ''}` +
          `${failedCount > 0 ? `, ❌ ${failedCount} failed: ${failedFiles.join(', ')}` : ''}`,
          { duration: 5000 }
        );
      } else if (duplicateCount === files.length) {
        toast.info(`All ${duplicateCount} file${duplicateCount > 1 ? 's are' : ' is'} already uploaded.`);
      } else if (stoppedCount > 0 && failedCount === 0) {
        // Keep the popup open so the user can retry or close it.
      } else if (stoppedCount > 0) {
        toast.error(
          `${failedCount} upload${failedCount > 1 ? 's' : ''} failed, ${stoppedCount} stopped.`,
          { duration: 6000 }
        );
      } else if (results.every(r => r.stopped || r.duplicate || r.result?.error === 'monthly_limit_reached')) {
        // The "Monthly limit reached" toast already explains it — a
        // "check your connection" message here would be misleading.
      } else {
        toast.error(`All ${files.length} uploads failed. Check your connection and try again.`, { duration: 6000 });
      }

    } catch (err) {
      toast.error(`Upload error: ${err.message || 'An unexpected error occurred.'}`);
    } finally {
      setIsUploading(false);
      setTimeout(() => setOverallProgress(0), 2000);
      event.target.value = '';
    }
  };

  // One file per request — fully independent, parallel-safe
  const uploadSingleFile = (file, jobId, adminEmail, url, aiEnabled, onProgress, options = {}) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const TIMEOUT_MS = 300_000; // 5 min per file
      const { onXhr, uploadId } = options;
      if (typeof onXhr === 'function') onXhr(xhr);

      const timeoutId = setTimeout(() => {
        xhr.abort();
        reject(new Error('Upload timeout — file may be too large or connection is slow'));
      }, TIMEOUT_MS);

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress((e.loaded / e.total) * 100);
      });

      xhr.addEventListener('load', () => {
        clearTimeout(timeoutId);
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { reject(new Error('Invalid server response')); }
        } else {
          reject(new Error(xhr.responseText || `Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => { clearTimeout(timeoutId); reject(new Error('Network error')); });
      xhr.addEventListener('abort', () => { clearTimeout(timeoutId); reject(new Error('Upload cancelled or timed out')); });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('job_id', jobId);
      formData.append('ai_processing_enabled', String(aiEnabled));
      formData.append('recruiterEmail', localStorage.getItem('employer_email'));
      if (uploadId) formData.append('upload_id', uploadId);
      const _cid = localStorage.getItem('companyAccountId');
      if (_cid && _cid !== 'null' && _cid !== 'undefined') {
        formData.append('company_id', _cid);
      }

      xhr.open('POST', url);
      xhr.withCredentials = true;
      xhr.setRequestHeader('X-Admin-Email', adminEmail || '');
      xhr.send(formData);
    });
  };

  const handleEvaluate = async (applicationId, force = false) => {
    try {
      setEvaluating(prev => ({ ...prev, [applicationId]: true }));

      const res = await fetch(getApiUrl('/api/evaluate_candidate'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ application_id: applicationId, force }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) throw new Error(data.error || 'Evaluation failed');

      toast.success(`AI evaluation complete — Score: ${data.final_score}`);
      refreshCandidates(); // re-fetch the list

    } catch (err) {
      toast.error(`Evaluation failed: ${err.message}`);
    } finally {
      setEvaluating(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  // Helper function to check if any score is below 40%
  const hasLowScore = (candidate) => {
    const scores = [
      candidate.Resume_Score,
      candidate.screening_overall_score,
      candidate.personality_overall_score,
      candidate.behavioral_overall_score,
      candidate.coding_overall_score
    ];

    // Check if any score exists and is below 40
    return scores.some(score => {
      if (score === null || score === undefined || score === '') return false;
      const numScore = Number(score);
      return !isNaN(numScore) && numScore < 40;
    });
  };

  // Fresh profiles must be visible in their tab regardless of score. Other
  // tabs retain the existing low-score grouping behaviour.
  const isNewProfilesTab = filters.status === 'new_profiles';
  const goodCandidates = isNewProfilesTab
    ? filteredCandidates
    : filteredCandidates.filter(candidate => !hasLowScore(candidate));
  const lowScoringCandidates = isNewProfilesTab
    ? []
    : filteredCandidates.filter(candidate => hasLowScore(candidate));

  // College gate — when the JD is set to "Require premium", candidates below
  // the bar are hidden by default (collapsed behind a toggle, NOT removed or
  // rejected, so a strong non-premium candidate is one click away).
  const collegeReq = jobDetails?.college_requirement;
  const belowCollegeBar = collegeReq?.mode === 'required'
    ? goodCandidates.filter(c => isBelowCollegeBar(c, collegeReq))
    : [];
  const visibleGoodCandidates = (showBelowCollegeBar || belowCollegeBar.length === 0)
    ? goodCandidates
    : goodCandidates.filter(c => !isBelowCollegeBar(c, collegeReq));

  // Pagination logic - use visible good candidates for pagination
  const indexOfLastCandidate = currentPage * candidatesPerPage;
  const indexOfFirstCandidate = indexOfLastCandidate - candidatesPerPage;
  const currentCandidates = visibleGoodCandidates.slice(indexOfFirstCandidate, indexOfLastCandidate);
  const totalPages = Math.ceil(visibleGoodCandidates.length / candidatesPerPage);

  // ── Per-candidate video-resume status (one bulk call per page) ──────────
  // {email: {has_video, score, verdict}} — feeds the 🎥 badge on each card.
  // videoStatusInFlightRef prevents the rapid-rerender duplicate calls the
  // state-based dedupe can't: the effect fires several times before the
  // first response lands (state not yet updated), so the same email list
  // was POSTed 2-3x back-to-back.
  const [videoStatuses, setVideoStatuses] = useState({});
  const videoStatusInFlightRef = useRef(new Set());
  useEffect(() => {
    const emails = [...new Set(
      currentCandidates
        .map(c => (c.candidate_email || c.email || '').toLowerCase())
        .filter(Boolean)
    )].filter(e => videoStatuses[e] === undefined && !videoStatusInFlightRef.current.has(e));
    if (!emails.length) return;
    emails.forEach(e => videoStatusInFlightRef.current.add(e));
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.post(
          getApiUrl('/api/video-resume/bulk-status'),
          { emails },
          { withCredentials: true }
        );
        if (!cancelled && resp.data?.statuses) {
          setVideoStatuses(prev => ({ ...prev, ...resp.data.statuses }));
        }
      } catch (e) {
        // Badge is best-effort — cards work without it. Allow a retry on
        // the next render pass by releasing the in-flight claim.
        emails.forEach(em => videoStatusInFlightRef.current.delete(em));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCandidates.map(c => c.candidate_email || c.email).join(',')]);

  // Reuse the existing profile-view API used by Company Admin. Updating the
  // hook first removes the profile from New Profiles without delaying access.
  const persistProfileOpened = async (candidate) => {
    markProfileOpened(candidate);

    const applicationId = candidate?.application_id ?? candidate?.id;
    if (applicationId == null || !jobId) return;

    const viewerEmail = (
      sessionStorage.getItem('admin_email') ||
      localStorage.getItem('admin_email') ||
      sessionStorage.getItem('employer_email') ||
      localStorage.getItem('employer_email') ||
      ''
    ).trim();

    try {
      const endpoint =
        `/api/job_applications/${encodeURIComponent(jobId)}/` +
        `${encodeURIComponent(applicationId)}/profile-view`;
      const response = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Email': viewerEmail,
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || 'Unable to record profile view.');
      }

      // Synchronise the server-backed flags for later filter changes/reloads.
      await refreshCandidates();
    } catch (viewError) {
      // The hook keeps this browser session responsive if persistence is
      // briefly unavailable, the same fallback used in the admin view.
      console.error('Unable to persist profile view:', viewError);
    }
  };

  // Handlers for actions
  const handleViewProfile = (candidate) => {
    void persistProfileOpened(candidate);
    setSelectedCandidate({
      ...candidate,
      is_profile_opened: true,
      is_fresh_profile: false,
    });
  };
  const handleCloseProfile = () => setSelectedCandidate(null);

  // ---- Applications Copilot bridges -------------------------------------
  // viewProfile(application_id): open the existing profile popup.
  const handleCopilotViewProfile = (applicationId) => {
    const target = findCandidateByApplicationId(candidates, applicationId);
    if (target) handleViewProfile({ ...target, job_id: jobId });
  };
  // Map the contract's filter keys (status, min_score, max_score, skills,
  // location, exp_min/exp_max, test_completed) onto this page's `filters`
  // state. Unknown keys are ignored; the page is reset to page 1.
  const handleCopilotApplyFilter = (f) => {
    if (!f || typeof f !== 'object') return;
    const mapped = {};
    // Backend tool filters use status[], skills_all/skills_any, location_contains,
    // min_exp/max_exp (see list_candidates in services/copilot/tools.py); accept those too.
    const status = Array.isArray(f.status) ? f.status[0] : f.status;
    if (status != null) mapped.status = String(status).toLowerCase();
    if (f.min_score != null) mapped.resumeScoreMin = String(f.min_score);
    if (f.max_score != null) mapped.resumeScoreMax = String(f.max_score);
    const skills = f.skills ?? f.skills_all ?? f.skills_any;
    if (skills != null) mapped.skills = Array.isArray(skills) ? skills.join(', ') : String(skills);
    const location = f.location ?? f.location_contains;
    if (location != null) mapped.location = String(location);
    if (f.exp_min != null || f.min_exp != null) mapped.experienceMin = String(f.exp_min ?? f.min_exp);
    if (f.exp_max != null || f.max_exp != null) mapped.experienceMax = String(f.exp_max ?? f.max_exp);
    if (f.test_completed != null) mapped.testCompleted = f.test_completed ? 'completed' : 'pending';
    if (f.has_test != null) mapped.testCompleted = 'completed';
    if (f.missing_test != null) mapped.testCompleted = 'pending';
    if (!Object.keys(mapped).length) return;
    setFilters((prev) => ({ ...prev, ...mapped }));
    setCurrentPage(1);
    setMainTab('applicants');
  };

  // Publish this page to the AI Assistant (EmployerLayout owns the dock).
  // Registered before the early returns below so the registration survives
  // loading / error states and is removed when the page unmounts.
  useAssistantPage({
    page: 'applications',
    jobId,
    jobDetails,
    candidates,
    visibleCandidates: currentCandidates,
    filters,
    searchQuery,
    mainTab,
    viewMode,
    selectedCandidate,
    onViewProfile: handleCopilotViewProfile,
    onLocate: locateApplicationCard,
    onApplyFilter: handleCopilotApplyFilter,
    onRefresh: refreshCandidates,
  });

  const handlePageChange = (pageNumber) => {
    setIsLoadingMore(true);
    setCurrentPage(pageNumber);
    // Simulate loading delay for better UX
    setTimeout(() => {
      setIsLoadingMore(false);
    }, 500);
  };
  
  // Refresh candidates when component mounts or jobId changes
  useEffect(() => {
    if (jobId && candidates.length === 0) {
      // Only refresh if we don't have cached data
      refreshCandidates();
    }
  }, [jobId, candidates.length, refreshCandidates]);

  // Listen for refreshCandidates event (from shortlist/reject actions)
  useEffect(() => {
    const handleRefreshCandidates = () => {
      refreshCandidates();
    };

    window.addEventListener('refreshCandidates', handleRefreshCandidates);
    
    return () => {
      window.removeEventListener('refreshCandidates', handleRefreshCandidates);
    };
  }, [refreshCandidates]);

  // When user clicks job title, show full job description view
  if (viewingJobDescription) {
    return (
      <JobDescriptionView
        job={{ ...viewingJobDescription, id: jobId, job_id: jobId }}
        onClose={() => setViewingJobDescription(null)}
        onSave={() => {
          setViewingJobDescription(null);
          // Refetch job details so header stays in sync
          axios.get(getApiUrl(`/api/job_description?jobId=${jobId}`), { withCredentials: true })
            .then((res) => setJobDetails(res.data))
            .catch(() => {});
        }}
      />
    );
  }

  // Show loading state only if we don't have any candidates and are actually loading
  // Don't unmount the Dialog during background refetches (e.g. focus event after file picker closes).
  // Only show skeleton on the true first load when no popup is open.
  if (loading && candidates.length === 0 && !showUploadPopup && !isUploading) {
    return <NoCandidatesSkeleton />;
  }

  // Show error state only if we don't have any cached candidates
  if (error && candidates.length === 0) {
    return (
      <Stack spacing="md" className="p-4">
        <Card>
          <CardContent className="p-6 text-center">
            <Text variant="destructive">Error: {error}</Text>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  // ── Inline Cheating Evidence view ─────────────────────────────────────
  // When the recruiter clicked the "Suspicious / Cheating Detected" chip
  // on a card, we render the dashboard INSIDE the same Stack shell so
  // the page layout (sidebar, header, etc.) stays put. The dashboard's
  // own Back button is wired to clear this state, restoring the
  // candidate list. We early-return BEFORE the candidate list so the
  // recruiter doesn't see both stacked.
  if (inlineEvidenceTestId) {
    return (
      <Stack spacing="md" className="w-full">
        <CheatingEvidence
          embedded
          testIdProp={inlineEvidenceTestId}
          typeProp={inlineEvidenceType}
          onClose={() => { setInlineEvidenceTestId(null); setInlineEvidenceType(null); }}
        />
      </Stack>
    );
  }

  return (
    <Stack spacing="md" className="w-full">
      {onBack && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="w-fit"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Overview
        </Button>
      )}
      <Card
        style={{
          backgroundColor: getColor('secondary'),
          borderColor: getColor('border'),
        }}
      >
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <Heading
                level="h2"
                as={jobDetails ? 'button' : undefined}
                type={jobDetails ? 'button' : undefined}
                className={cn(
                  "mb-3",
                  jobDetails && "cursor-pointer hover:opacity-80 transition-opacity text-left outline-none focus:ring-2 focus:ring-primary/20 rounded"
                )}
                onClick={() => jobDetails && setViewingJobDescription(jobDetails)}
              >
                {jobDetails?.job_title || 'Job Applications'}
              </Heading>
              <Inline spacing="md" wrap className="mb-3">
                {can('candidates.add') && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => { setMainTab('applicants'); setShowUploadPopup(true); }}
                  style={{
                    backgroundColor: getColor('primary'),
                    color: getColor('primaryForeground'),
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Resume
                </Button>
                )}
                
                <Button
                  variant={showFilters ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className={cn(
                    "relative",
                    showFilters && "bg-accent text-accent-foreground hover:bg-accent/80 border border-primary/20"
                  )}
                  style={showFilters ? {
                    backgroundColor: getColor('accent'),
                    color: getColor('accentForeground'),
                    borderColor: `hsl(${getColor('primary')} / 0.2)`,
                  } : {}}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                  {(Object.values(filters).some(value => value !== '' && value !== 'all') || searchQuery.trim()) && (
                    <span className={cn(
                      "absolute -top-1 -right-1 w-2 h-2 rounded-full",
                      showFilters ? "bg-primary" : "bg-primary"
                    )} />
                  )}
                </Button>
                
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search candidates..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-10"
                  />
                  {searchQuery.trim() && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => setSearchQuery('')}
                      title="Clear search"
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                
                {/* View Mode Toggle */}
                <Tabs value={viewMode} onValueChange={setViewMode}>
                  <TabsList className="h-8">
                    <TabsTrigger value="list" className="h-7 px-2 gap-1.5" title="List view">
                      <List className="h-4 w-4" />
                      <span className="hidden sm:inline text-xs">List</span>
                    </TabsTrigger>
                    <TabsTrigger value="grid" className="h-7 px-2 gap-1.5" title="Grid view">
                      <LayoutGrid className="h-4 w-4" />
                      <span className="hidden sm:inline text-xs">Grid</span>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </Inline>

              {/* Status row (more noticeable + aligned) */}
              <Inline spacing="sm" align="center" justify="start" wrap className="mb-3">
                <Text size="xs" variant="muted" className="font-medium min-w-[52px]">
                  Status
                </Text>
                <Tabs
                  value={filters.status || "all"}
                  onValueChange={(val) => {
                    setFilters((prev) => ({ ...prev, status: val }));
                    setCurrentPage(1);
                  }}
                >
                  <TabsList
                    className={cn(
                      "h-9",
                      (filters.status || "all") !== "all" && "ring-1 ring-primary/25"
                    )}
                  >
                    <TabsTrigger value="all" className="h-8 px-3 text-xs">
                      All
                    </TabsTrigger>
                    <TabsTrigger value="new_profiles" className="h-8 px-3 text-xs">
                      New Profiles ({candidates.filter(isFreshProfile).length})
                    </TabsTrigger>
                    <TabsTrigger value="viewed" className="h-8 px-3 text-xs">
                      Viewed Profiles
                    </TabsTrigger>
                    <TabsTrigger value="shortlisted" className="h-8 px-3 text-xs">
                      Shortlisted
                    </TabsTrigger>
                    <TabsTrigger value="rejected" className="h-8 px-3 text-xs">
                      Rejected
                    </TabsTrigger>
                    <TabsTrigger value="interview" className="h-8 px-3 text-xs">
                      Interview
                    </TabsTrigger>
                    <TabsTrigger value="selected" className="h-8 px-3 text-xs">
                      Selected Candidates
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </Inline>
              <Inline spacing="md" wrap className="text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  {jobDetails?.location || 'Location not specified'}
                </div>
                <div className="flex items-center gap-1.5">
                  <Briefcase className="w-4 h-4" />
                  {jobDetails?.job_type || 'Full-time'}
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  {jobDetails?.experience_range || 'Experience not specified'}
                </div>

                {/* Broadcast — publish the job and manage where it is
                    listed / shared. Always available; the dialog itself
                    explains what the signed-in user may change. */}
                {scopedRolesEnabled && can('jobs.assign') && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPeople(true)}
                      className="h-7 px-2 gap-1"
                      title="Choose who works on this job"
                    >
                      <UsersRound className="w-3.5 h-3.5" />
                      People
                    </Button>
                    <JobPeopleDialog
                      jobId={jobId}
                      jobTitle={jobDetails?.job_title}
                      open={showPeople}
                      onOpenChange={setShowPeople}
                    />
                  </>
                )}
                {can('jobs.broadcast') && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBroadcast(true)}
                  className="h-7 px-2 gap-1"
                  title="Publish this job and choose where it is listed"
                >
                  <Megaphone className="w-3.5 h-3.5" />
                  Broadcast
                </Button>
                )}
                <BroadcastDialog
                  jobId={jobId}
                  open={showBroadcast}
                  onOpenChange={setShowBroadcast}
                  onStateChange={(state) => {
                    // Keep the inline public link in step with a publish /
                    // unpublish done from the dialog.
                    setIsJobPublic(Boolean(state?.channels?.live));
                    if (state?.job?.company_name) {
                      setCompanyName(state.job.company_name);
                      setCompanySlug(slugifyCompany(state.job.company_name));
                    }
                  }}
                />

                {/* Public job link — sits inline in the meta row (uses the
                    free space after experience). Only shown when this job
                    is toggled public on the board. */}
                {isJobPublic && publicJobUrl && (
                  <div className="flex items-center gap-1.5">
                    <Globe className="w-4 h-4 text-emerald-600" />
                    <Input
                      readOnly
                      value={publicJobUrl}
                      onFocus={(e) => e.target.select()}
                      className="h-7 text-xs w-[220px] bg-background"
                      aria-label="Public job link"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCopyPublicLink}
                      className="h-7 px-2 gap-1"
                      title="Copy public job link"
                    >
                      {linkCopied ? (
                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="w-3.5 h-3.5" />
                      )}
                      {linkCopied ? 'Copied' : 'Copy'}
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          className="h-7 px-2 gap-1"
                          title="Share this job"
                          style={{
                            backgroundColor: getColor('primary'),
                            color: getColor('primaryForeground'),
                          }}
                        >
                          <Share2 className="w-3.5 h-3.5" />
                          Share
                          <ChevronDown className="w-3 h-3 opacity-80" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => handleShareTo('whatsapp')} className="cursor-pointer">
                          <MessageCircle className="mr-2 h-4 w-4 text-emerald-600" />
                          WhatsApp
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleShareTo('linkedin')} className="cursor-pointer">
                          <Linkedin className="mr-2 h-4 w-4 text-[#0A66C2]" />
                          LinkedIn
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleShareTo('telegram')} className="cursor-pointer">
                          <Send className="mr-2 h-4 w-4 text-sky-500" />
                          Telegram
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleShareTo('twitter')} className="cursor-pointer">
                          <Twitter className="mr-2 h-4 w-4 text-sky-400" />
                          X (Twitter)
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleShareTo('email')} className="cursor-pointer">
                          <Mail className="mr-2 h-4 w-4 text-muted-foreground" />
                          Email
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleCopyShareMessage} className="cursor-pointer">
                          {messageCopied ? (
                            <Check className="mr-2 h-4 w-4 text-emerald-600" />
                          ) : (
                            <Copy className="mr-2 h-4 w-4" />
                          )}
                          {messageCopied ? 'Copied' : 'Copy message'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </Inline>
            </div>
            <Card className="p-4 text-center border-2">
              <Text size="2xl" className="font-bold text-primary mb-1">
                {filteredCandidates.length}
              </Text>
              <Text size="xs" variant="muted" className="uppercase tracking-wide">
                Candidates
              </Text>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Two views: existing applicants vs auto-sourced recommendations */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
        <TabsList>
          <TabsTrigger value="applicants">Applicants</TabsTrigger>
          <TabsTrigger value="recommended" className="gap-1.5">
            <Sparkles className="w-4 h-4" />
            Recommended
          </TabsTrigger>
          <TabsTrigger value="trends" className="gap-1.5">
            <TrendingUp className="w-4 h-4" />
            Trends
          </TabsTrigger>
        </TabsList>

        <TabsContent value="applicants" className="mt-4 space-y-4">

      {/* Filter Toolbar */}
      {showFilters && (
        <Card>
          <CardContent className="p-3">
            <div className="space-y-3">
              <div>
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Candidate</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="min-w-[180px] flex-1">
                    <Label htmlFor="skills-filter" className="mb-1.5 block text-xs">Skills</Label>
                    <div className="relative">
                      <Input
                        id="skills-filter"
                        type="text"
                        placeholder="e.g., React, Python"
                        value={filters.skills}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFilters((prev) => ({ ...prev, skills: val }));
                        }}
                        className="h-9 pr-8 text-sm"
                      />
                      {filters.skills && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                          onClick={() => clearFilter('skills')}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="w-[170px]">
                    <Label htmlFor="college-tier-filter" className="mb-1.5 block text-xs">College Tier</Label>
                    <Select
                      value={filters.collegeTierFilter || 'all'}
                      onValueChange={(val) => {
                        setFilters((prev) => ({ ...prev, collegeTierFilter: val }));
                      }}
                    >
                      <SelectTrigger id="college-tier-filter" className="h-9 text-sm">
                        <SelectValue placeholder="All Colleges" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Colleges</SelectItem>
                        <SelectItem value="premium">Premium (Tier 1 + 2)</SelectItem>
                        <SelectItem value="tier1">Tier 1 only</SelectItem>
                        <SelectItem value="tier2">Tier 2 only</SelectItem>
                        <SelectItem value="none">Not tiered</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-[110px]">
                    <Label htmlFor="experience-min-filter" className="mb-1.5 block text-xs">Min Experience</Label>
                    <Input
                      id="experience-min-filter"
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="Min"
                      value={filters.experienceMin}
                      onChange={(e) => setFilters((prev) => ({ ...prev, experienceMin: e.target.value }))}
                      className="h-9 text-sm"
                    />
                  </div>

                  <div className="w-[110px]">
                    <Label htmlFor="experience-max-filter" className="mb-1.5 block text-xs">Max Experience</Label>
                    <Input
                      id="experience-max-filter"
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="Max"
                      value={filters.experienceMax}
                      onChange={(e) => setFilters((prev) => ({ ...prev, experienceMax: e.target.value }))}
                      className="h-9 text-sm"
                    />
                  </div>

                  <div className="w-[160px]">
                    <Label htmlFor="location-filter" className="mb-1.5 block text-xs">Current Location</Label>
                    <div className="relative">
                      <Input
                        id="location-filter"
                        type="text"
                        placeholder="e.g., Bangalore"
                        value={filters.location}
                        onChange={(e) => setFilters((prev) => ({ ...prev, location: e.target.value }))}
                        className="h-9 pr-8 text-sm"
                      />
                      {filters.location && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0"
                          onClick={() => clearFilter('location')}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="w-[160px]">
                    <Label htmlFor="applied-date-filter" className="mb-1.5 block text-xs">Applied Date</Label>
                    <Select
                      value={filters.appliedDateFilter || 'all'}
                      onValueChange={(val) => {
                        setFilters((prev) => ({ ...prev, appliedDateFilter: val }));
                      }}
                    >
                      <SelectTrigger id="applied-date-filter" className="h-9 text-sm">
                        <SelectValue placeholder="All Time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Time</SelectItem>
                        <SelectItem value="today">Today</SelectItem>
                        <SelectItem value="7days">Last 7 days</SelectItem>
                        <SelectItem value="30days">Last 30 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Evaluation</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-[180px]">
                    <Label htmlFor="score-filter-type" className="mb-1.5 block text-xs">Score Type</Label>
                    <Select
                      value={filters.scoreFilterType || 'none'}
                      onValueChange={(val) => {
                        setFilters((prev) => ({ ...prev, scoreFilterType: val === 'none' ? '' : val }));
                      }}
                    >
                      <SelectTrigger id="score-filter-type" className="h-9 text-sm">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="Resume_Score">Resume Score</SelectItem>
                        <SelectItem value="screening_overall_score">Screening Test</SelectItem>
                        <SelectItem value="personality_overall_score">Personality Test</SelectItem>
                        <SelectItem value="behavioral_overall_score">Behavioral Test</SelectItem>
                        <SelectItem value="coding_overall_score">Coding Test</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-[100px]">
                    <Label htmlFor="min-score" className="mb-1.5 block text-xs">Min Score</Label>
                    <Input
                      id="min-score"
                      type="number"
                      min="0"
                      max="100"
                      placeholder="Min"
                      value={filters.minScore || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFilters((prev) => ({ ...prev, minScore: v }));
                      }}
                      className="h-9 text-sm"
                    />
                  </div>

                  <div className="w-[100px]">
                    <Label htmlFor="max-score" className="mb-1.5 block text-xs">Max Score</Label>
                    <Input
                      id="max-score"
                      type="number"
                      min="0"
                      max="100"
                      placeholder="Max"
                      value={filters.maxScore || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFilters((prev) => ({ ...prev, maxScore: v }));
                      }}
                      className="h-9 text-sm"
                    />
                  </div>

                  <div className="w-[140px]">
                    <Label htmlFor="test-status" className="mb-1.5 block text-xs">Test Status</Label>
                    <Select
                      value={filters.testCompleted || 'all'}
                      onValueChange={(val) => setFilters((prev) => ({ ...prev, testCompleted: val }))}
                    >
                      <SelectTrigger id="test-status" className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-[150px]">
                    <Label htmlFor="relevance-filter" className="mb-1.5 block text-xs">Relevance</Label>
                    <Select
                      value={filters.relevanceFilter || 'all'}
                      onValueChange={(val) => {
                        setFilters((prev) => ({ ...prev, relevanceFilter: val }));
                      }}
                    >
                      <SelectTrigger id="relevance-filter" className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="great">Great (≥80)</SelectItem>
                        <SelectItem value="good">Good (≥50)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-[150px]">
                    <Label htmlFor="interview-filter" className="mb-1.5 block text-xs">Interview Status</Label>
                    <Select
                      value={filters.interviewFilter || 'all'}
                      onValueChange={(val) => {
                        setFilters((prev) => ({ ...prev, interviewFilter: val === 'all' ? '' : val }));
                      }}
                    >
                      <SelectTrigger id="interview-filter" className="h-9 text-sm">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="1">Yes</SelectItem>
                        <SelectItem value="0">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Source</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div className="w-[160px]">
                    <Label htmlFor="uploader-filter" className="mb-1.5 block text-xs">Uploaded By</Label>
                    <Select
                      value={filters.uploaderFilter || 'all'}
                      onValueChange={(val) => {
                        setFilters((prev) => ({ ...prev, uploaderFilter: val }));
                      }}
                    >
                      <SelectTrigger id="uploader-filter" className="h-9 text-sm">
                        <SelectValue placeholder="All Sources" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Sources</SelectItem>
                        <SelectItem value="myself">Myself</SelectItem>
                        <SelectItem value="recruiter">Recruiter (others)</SelectItem>
                        <SelectItem value="company">Company / Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Sorting</p>
                  <div className="w-[180px]">
                    <Label htmlFor="sort-by" className="mb-1.5 block text-xs">Sort By</Label>
                    <Select
                      value={filters.sortingCriteria || 'Resume_Score'}
                      onValueChange={(val) => {
                        setFilters((prev) => ({ ...prev, sortingCriteria: val }));
                      }}
                    >
                      <SelectTrigger id="sort-by" className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Resume_Score">Resume Score</SelectItem>
                        <SelectItem value="relevance">Relevance</SelectItem>
                        <SelectItem value="upload_date_desc">Newest Uploaded</SelectItem>
                        <SelectItem
                          value="upload_date_asc"
                          disabled={isNewProfilesTab}
                        >
                          Oldest Uploaded
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {(Object.values(filters).some((value) => value !== '' && value !== 'all' && value !== null && value !== undefined) || searchQuery.trim()) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                    className="h-9 text-xs"
                  >
                    <X className="mr-1 h-3 w-3" />
                    Clear All
                  </Button>
                )}
              </div>

              {(Object.values(filters).some((value) => value !== '' && value !== 'all') || searchQuery.trim()) && (
                <div className="flex flex-wrap items-center gap-2 border-t pt-2">
                  <Text size="xs" variant="muted" className="font-medium">Active:</Text>
                  {searchQuery.trim() && (
                    <Badge variant="secondary" className="h-6 gap-1 text-xs">
                      Search
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-3 w-3 p-0 hover:bg-transparent"
                        onClick={() => setSearchQuery('')}
                      >
                        <X className="h-2.5 w-2.5" />
                      </Button>
                    </Badge>
                  )}
                  {Object.entries(filters).map(([key, value]) => {
                    if (value === '' || value === 'all' || value === null || value === undefined) return null;
                    if (key === 'sortingCriteria' && value === 'Resume_Score') return null;
                    if (key === 'minScore' || key === 'maxScore') return null;

                    if (key === 'experienceMin' || key === 'experienceMax') {
                      // Render one combined chip from the first non-empty field we hit.
                      if (key === 'experienceMax' && filters.experienceMin) return null;
                      if (key === 'experienceMin' && !filters.experienceMin && filters.experienceMax) return null;
                      const min = filters.experienceMin;
                      const max = filters.experienceMax;
                      let label = null;
                      if (min && max) label = `Experience: ${min}–${max} yrs`;
                      else if (min) label = `Experience: ≥${min} yrs`;
                      else if (max) label = `Experience: ≤${max} yrs`;
                      if (!label) return null;
                      return (
                        <Badge key="experience-range" variant="secondary" className="h-6 gap-1 text-xs">
                          {label}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-3 w-3 p-0 hover:bg-transparent"
                            onClick={() => {
                              setFilters((prev) => ({
                                ...prev,
                                experienceMin: '',
                                experienceMax: '',
                              }));
                            }}
                          >
                            <X className="h-2.5 w-2.5" />
                          </Button>
                        </Badge>
                      );
                    }

                    if (key === 'testCompleted') {
                      return (
                        <Badge key={key} variant="secondary" className="h-6 gap-1 text-xs">
                          <span className="flex items-center gap-1">
                            {value === 'completed' ? 'Test Status: Completed' : 'Test Status: Pending'}
                            {value === 'completed' ? (
                              <CircleCheck className="h-3.5 w-3.5" />
                            ) : (
                              <Clock className="h-3 w-3" />
                            )}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-3 w-3 p-0 hover:bg-transparent"
                            onClick={() => {
                              setFilters((prev) => ({ ...prev, testCompleted: 'all' }));
                            }}
                          >
                            <X className="h-2.5 w-2.5" />
                          </Button>
                        </Badge>
                      );
                    }

                    const sortLabels = {
                      Resume_Score: 'Resume Score',
                      relevance: 'Relevance',
                      upload_date_desc: 'Newest Uploaded',
                      upload_date_asc: 'Oldest Uploaded',
                      // Legacy keys still resolve if somehow set
                      screening_overall_score: 'Screening Test',
                      personality_overall_score: 'Personality Test',
                      behavioral_overall_score: 'Behavioral Test',
                      coding_overall_score: 'Coding Test',
                      rank: 'Candidate Rank',
                    };
                    const scoreLabels = {
                      Resume_Score: 'Resume Score',
                      screening_overall_score: 'Screening Test',
                      personality_overall_score: 'Personality Test',
                      behavioral_overall_score: 'Behavioral Test',
                      coding_overall_score: 'Coding Test',
                    };
                    const uploaderLabels = {
                      myself: 'Myself',
                      recruiter: 'Recruiter (others)',
                      company: 'Company / Admin',
                    };
                    const collegeLabels = {
                      premium: 'Premium (Tier 1 + 2)',
                      tier1: 'Tier 1 only',
                      tier2: 'Tier 2 only',
                      none: 'Not tiered',
                    };
                    const appliedLabels = {
                      today: 'Today',
                      '7days': 'Last 7 days',
                      '30days': 'Last 30 days',
                    };

                    const friendly = (() => {
                      switch (key) {
                        case 'skills': return `Skills: ${value}`;
                        case 'location': return `Location: ${value}`;
                        case 'status': return `Status: ${String(value).charAt(0).toUpperCase()}${String(value).slice(1)}`;
                        case 'sortingCriteria': return `Sorted: ${sortLabels[value] || value}`;
                        case 'relevanceFilter': return value === 'great' ? 'Relevance: ≥80' : 'Relevance: ≥50';
                        case 'scoreFilterType': {
                          const scoreName = scoreLabels[value] || 'Score';
                          const min = filters.minScore;
                          const max = filters.maxScore;
                          if (min && max) return `Score Type: ${scoreName} ${min}–${max}`;
                          if (min) return `Score Type: ${scoreName} ≥${min}`;
                          if (max) return `Score Type: ${scoreName} ≤${max}`;
                          return `Score Type: ${scoreName}`;
                        }
                        case 'interviewFilter': return value === '1' ? 'Interview Status: Yes' : 'Interview Status: No';
                        case 'uploaderFilter': return `Uploaded By: ${uploaderLabels[value] || value}`;
                        case 'collegeTierFilter': return `College: ${collegeLabels[value] || value}`;
                        case 'appliedDateFilter': return `Applied: ${appliedLabels[value] || value}`;
                        default: return null;
                      }
                    })();
                    if (!friendly) return null;
                    return (
                      <Badge key={key} variant="secondary" className="h-6 gap-1 text-xs">
                        {friendly}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-3 w-3 p-0 hover:bg-transparent"
                          onClick={() => {
                            const updater = { ...filters };
                            if (key === 'resumeScoreMin') updater.resumeScoreMin = '';
                            if (key === 'resumeScoreMax') updater.resumeScoreMax = '';
                            if (key === 'minScore') updater.minScore = '';
                            if (key === 'maxScore') updater.maxScore = '';
                            if (key === 'skillQuery') updater.skillQuery = '';
                            else if (key === 'sortingCriteria') updater.sortingCriteria = 'Resume_Score';
                            else if (key === 'relevanceFilter' || key === 'uploaderFilter' || key === 'collegeTierFilter' || key === 'appliedDateFilter') updater[key] = 'all';
                            else if (key === 'scoreFilterType') {
                              updater.scoreFilterType = '';
                              updater.minScore = '';
                              updater.maxScore = '';
                            } else updater[key] = '';
                            setFilters(updater);
                          }}
                        >
                          <X className="h-2.5 w-2.5" />
                        </Button>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Resume Popup */}
      <Dialog open={showUploadPopup} onOpenChange={(open) => !isUploading && setShowUploadPopup(open)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5" />
              Upload Candidate Resume{uploadFilesStatus.length > 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              Upload candidate resume(s) to automatically parse and enroll them for this job
            </DialogDescription>
            <CreditWidget balance={balance}/>
          </DialogHeader>

          <div className="space-y-6">

            {/* ── AI Processing Toggle ── */}
            {!isUploading && (
              <div className="flex items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <Text className="font-medium text-sm">AI Resume Analysis</Text>
                  </div>
                  <Text size="xs" variant="muted">
                    {aiProcessingEnabled
                      ? 'Resumes will be scored, ranked, and analysed automatically'
                      : 'Resumes will be uploaded without AI scoring — you can analyse later'}
                  </Text>
                </div>
                <Switch
                  checked={aiProcessingEnabled}
                  onCheckedChange={setAiProcessingEnabled}
                  disabled={isUploading}
                />
              </div>
            )}

            {aiProcessingEnabled && costPerResume != null && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700 border border-amber-200">
                <Coins className="w-3 h-3" />
                {costPerResume} credit / resume
              </span>
            )}

            {/* Show overall progress when uploading multiple files */}
            {isUploading && uploadFilesStatus.length > 1 && (
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Text size="sm" className="font-medium flex items-center gap-2">
                      {(() => {
                        const currentFile = uploadFilesStatus.find(f => f.status === 'uploading' || f.status === 'processing');
                        const completedCount = uploadFilesStatus.filter(f => ['success','duplicate','failed','stopped'].includes(f.status)).length;
                        if (currentFile?.status === 'uploading') return (<><FileUp className="w-4 h-4" /><span className="min-w-0 flex-1 truncate" title={currentFile.name}>Uploading: {middleEllipsisFilename(currentFile.name)}</span></>);
                        if (currentFile?.status === 'processing') return (<><Settings className="w-4 h-4 animate-spin" /><span className="min-w-0 flex-1 truncate" title={currentFile.name}>{aiProcessingEnabled ? 'Analysing' : 'Processing'}: {middleEllipsisFilename(currentFile.name)}</span></>);
                        if (completedCount === uploadFilesStatus.length) return (<><CheckCircle2 className="w-4 h-4 text-green-600" /><span>All files processed!</span></>);
                        return null;
                      })()}
                    </Text>
                    <Text size="sm" className="font-semibold">{overallProgress}%</Text>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-primary transition-all duration-300" style={{ width: `${overallProgress}%` }} />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* File input */}
            {!isUploading && (
              <>
                <input
                  type="file"
                  id="resume-upload-popup"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleResumeUpload}
                  disabled={isUploading}
                  className="hidden"
                  multiple
                />
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('resume-upload-popup')?.click()}
                >
                  <Card className={cn(
                    "border-2 border-dashed transition-colors cursor-pointer",
                    isDragging ? "border-primary bg-primary/5" : "hover:border-primary"
                  )}>
                    <CardContent className="p-8 flex flex-col items-center justify-center gap-4 text-center">
                      <Upload className="w-12 h-12 text-muted-foreground" />
                      <div className="space-y-1">
                        <Text className="font-semibold">Click to Select Resume(s)</Text>
                        <Text size="sm" variant="muted">or drag and drop here</Text>
                      </div>
                      {/* AI status hint inside drop zone */}
                      <div className={cn(
                        "flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full",
                        aiProcessingEnabled
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground"
                      )}>
                        <Sparkles className="w-3 h-3" />
                        <span>{aiProcessingEnabled ? 'AI analysis ON' : 'AI analysis OFF'}</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1.5">
                          <File className="w-3.5 h-3.5" />
                          <span>PDF, DOC, DOCX, TXT</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Folder className="w-3.5 h-3.5" />
                          <span>Multiple files (max 10)</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}

            {/* Per-file status list — unchanged, keep yours as-is */}
            {uploadFilesStatus.length > 0 && (
              <div className="space-y-3">
                <Heading level="h4" size="sm">
                  {isUploading ? 'Upload Progress:' : 'Upload Results:'}
                </Heading>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {uploadFilesStatus.map((f, idx) => (
                    <Card key={idx} className={cn(
                      "transition-colors",
                      f.status === 'success' && "border-green-200 bg-green-50/50",
                      f.status === 'failed' && "border-red-200 bg-red-50/50",
                      f.status === 'duplicate' && "border-amber-200 bg-amber-50/50",
                      f.status === 'stopped' && "border-orange-200 bg-orange-50/50"
                    )}>
                      <CardContent className="p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {f.status === 'success' && <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />}
                            {f.status === 'failed' && <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />}
                            {f.status === 'duplicate' && <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />}
                            {f.status === 'stopped' && <Ban className="w-4 h-4 text-orange-600 flex-shrink-0" />}
                            {f.status === 'queued' && <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                            {['uploading','processing','finalizing','preparing'].includes(f.status) && (
                              <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                            )}
                            <Text size="sm" className="truncate" title={f.name}>
                              {middleEllipsisFilename(f.name)}
                            </Text>
                          </div>
                          <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                            {/* Show AI badge per file based on what was used */}
                            {(f.status === 'success' || f.status === 'duplicate') && (
                              <span className={cn(
                                "text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1",
                                f.aiProcessed
                                  ? "bg-primary/10 text-primary"
                                  : "bg-muted text-muted-foreground"
                              )}>
                                <Sparkles className="w-2.5 h-2.5" />
                                {f.aiProcessed ? 'AI' : 'No AI'}
                              </span>
                            )}
                            <Badge
                              variant={
                                f.status === 'success' ? 'default' :
                                f.status === 'failed' ? 'destructive' :
                                f.status === 'duplicate' ? 'secondary' : 'outline'
                              }
                              className={f.status === 'stopped' ? 'border-orange-300 text-orange-700 bg-orange-50' : undefined}
                            >
                              {f.status === 'success' && 'Success'}
                              {f.status === 'failed' && 'Failed'}
                              {f.status === 'duplicate' && 'Duplicate'}
                              {f.status === 'stopped' && 'Upload stopped'}
                              {f.status === 'queued' && 'Queued'}
                              {f.status === 'uploading' && 'Uploading'}
                              {f.status === 'processing' && (aiProcessingEnabled ? 'Analysing' : 'Processing')}
                              {f.status === 'finalizing' && 'Finalizing'}
                              {f.status === 'preparing' && 'Preparing'}
                            </Badge>
                            {ACTIVE_RESUME_UPLOAD_STATUSES.includes(f.status) && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 px-2.5 text-[11px] font-medium border-orange-400 text-orange-700 hover:bg-orange-50 hover:text-orange-800 hover:border-orange-500"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  stopResumeUpload(idx);
                                }}
                              >
                                Stop Upload
                              </Button>
                            )}
                          </div>
                        </div>

                        {['uploading','processing','finalizing','preparing'].includes(f.status) && (
                          <div className="space-y-1">
                            <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
                              <div className="h-full bg-primary transition-all duration-300" style={{ width: `${f.progress}%` }} />
                            </div>
                            <Text size="xs" variant="muted" className="text-right">{f.progress}%</Text>
                          </div>
                        )}

                        {f.error && (
                          f.status === 'duplicate' ? (
                            <div className="flex items-start gap-2 rounded-md bg-amber-50 p-2">
                              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                              <Text size="xs" className="flex-1 text-amber-800">{f.error}</Text>
                            </div>
                          ) : (
                            <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-2">
                              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
                              <Text size="xs" variant="destructive" className="flex-1">{f.error}</Text>
                            </div>
                          )
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {!isUploading && uploadFilesStatus.length > 0 && (
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowUploadPopup(false); setUploadFilesStatus([]); }}>
                Close
              </Button>
              <Button
                onClick={() => {
                  setUploadFilesStatus([]);
                  document.getElementById('resume-upload-popup')?.click();
                }}
                style={{ backgroundColor: getColor('primary'), color: getColor('primaryForeground') }}
              >
                Upload More
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Render candidate cards */}
      {loading && currentCandidates.length === 0 && lowScoringCandidates.length === 0 ? (
        <NoCandidatesSkeleton />
      ) : currentCandidates.length === 0 && lowScoringCandidates.length === 0 && belowCollegeBar.length > 0 ? (
        /* All candidates are hidden by the "Require premium" college gate —
           give a clear reveal button instead of a bare "No candidates found". */
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 px-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/40 mb-4">
              <AlertCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {belowCollegeBar.length} candidate{belowCollegeBar.length !== 1 ? 's' : ''} hidden by the college requirement
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-sm mb-6">
              This role is set to “Require premium”, so candidates whose college doesn’t meet the bar are hidden by default. They haven’t been rejected — you can still review them.
            </p>
            <Button
              onClick={() => setShowBelowCollegeBar(true)}
              style={{
                backgroundColor: getColor('primary'),
                color: getColor('primaryForeground'),
                borderColor: getColor('primary'),
              }}
              className="gap-2"
            >
              Show {belowCollegeBar.length} hidden candidate{belowCollegeBar.length !== 1 ? 's' : ''}
            </Button>
          </CardContent>
        </Card>
      ) : currentCandidates.length === 0 && lowScoringCandidates.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 px-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">
              No candidates found
            </h3>
            <p className="text-sm text-muted-foreground text-center max-w-sm mb-6">
              {searchQuery.trim() || Object.values(filters).some(value => value !== '' && value !== 'all') 
                ? 'Try adjusting your search or filters to see more results.'
                : 'No candidates have applied for this job yet. Upload resumes to get started.'}
            </p>
            {!(searchQuery.trim() || Object.values(filters).some(value => value !== '' && value !== 'all')) && (
              <Button
                onClick={() => setShowUploadPopup(true)}
                style={{
                  backgroundColor: getColor('primary'),
                  color: getColor('primaryForeground'),
                  borderColor: getColor('primary')
                }}
                className="gap-2"
              >
                <Upload className="h-4 w-4" />
                Upload Resume
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* College gate banner — appears only for "Require premium" JDs that
              have candidates below the bar. They're hidden, not removed. */}
          {belowCollegeBar.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 dark:border-amber-900 dark:bg-amber-950/30">
              <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>
                  <span className="font-semibold">{belowCollegeBar.length}</span>
                  {' '}candidate{belowCollegeBar.length !== 1 ? 's' : ''} below this role&apos;s college requirement{' '}
                  {showBelowCollegeBar ? 'shown below' : 'hidden'}.
                </span>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-300 dark:hover:bg-amber-900/40"
                onClick={() => setShowBelowCollegeBar(v => !v)}
              >
                {showBelowCollegeBar ? 'Hide them' : 'Show all'}
              </Button>
            </div>
          )}

          {/* Good Candidates (scores >= 40% or N/A) */}
          <div className={cn(
            viewMode === 'list' && "space-y-4",
            viewMode === 'grid' && "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          )}>
            {currentCandidates.map((candidate, index) => (
              <React.Fragment key={candidate.email || index}>
                {/* data-application-id lets the Copilot "Locate" action scroll to this card */}
                <div data-application-id={candidate.application_id ?? candidate.id} className="h-full">
                <CandidateCard
                  candidate={{
                    ...candidate,
                    job_id: jobId,
                    is_profile_opened: Boolean(candidate.is_profile_opened || !isFreshProfile(candidate)),
                  }}
                  onViewProfile={() => handleViewProfile({...candidate, job_id: jobId})}
                  onViewResume={() => void persistProfileOpened({...candidate, job_id: jobId})}
                  jobDetails={jobDetails}
                  viewMode={viewMode}
                  videoStatus={videoStatuses[(candidate.candidate_email || candidate.email || '').toLowerCase()]}
                  nextRoundCandidates={shortlistedMeetingCandidates}
                  evaluating={evaluating}
                  handleEvaluate={handleEvaluate}
                  costPerResume={costPerResume}
                  // When the chip is clicked, flip our inline view
                  // state instead of navigating. Setting this state
                  // causes the inline-CheatingEvidence branch at the
                  // top of the return to render in place of the list.
                  onOpenCheatingEvidence={openInlineEvidence}
                />
                </div>
              </React.Fragment>
            ))}
          </div>

          {isLoadingMore && <LoadingMoreCandidatesSkeleton />}

          {/* Pagination Info */}
          <div className="text-center py-4">
            <Text size="sm" variant="muted">
              {goodCandidates.length === 0
                ? 'No candidates match the current filters.'
                : `Showing ${indexOfFirstCandidate + 1}-${Math.min(indexOfLastCandidate, goodCandidates.length)} of ${goodCandidates.length} candidates`}
              {lowScoringCandidates.length > 0 && (
                <span className="text-muted-foreground">
                  {' '}({lowScoringCandidates.length} candidate{lowScoringCandidates.length === 1 ? '' : 's'} below 40% {lowScoringCandidates.length === 1 ? 'is' : 'are'} hidden)
                </span>
              )}
            </Text>
          </div>
          
          {/* Pagination */}
          {goodCandidates.length > candidatesPerPage && (
            <Pagination 
              totalPages={totalPages} 
              currentPage={currentPage} 
              onPageChange={handlePageChange} 
            />
          )}

          {/* Divider with toggle for low-scoring candidates */}
          {lowScoringCandidates.length > 0 && (
            <>
              <div 
                className="flex items-center gap-4 cursor-pointer py-4 hover:opacity-80 transition-opacity"
                onClick={() => setShowLowScoringCandidates(!showLowScoringCandidates)}
              >
                <Separator className="flex-1" />
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-destructive" />
                  <Text size="sm" variant="destructive" className="font-medium">
                    {lowScoringCandidates.length} Candidate{lowScoringCandidates.length > 1 ? 's' : ''} with Scores Below 40%
                  </Text>
                  <ChevronDown className={cn(
                    "w-4 h-4 text-destructive transition-transform",
                    showLowScoringCandidates && "rotate-180"
                  )} />
                </div>
                <Separator className="flex-1" />
              </div>

              {/* Low-scoring candidates (collapsible) */}
              {showLowScoringCandidates && (
                <div className={cn(
                  viewMode === 'list' && "space-y-4",
                  viewMode === 'grid' && "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                )}>
                  {lowScoringCandidates.map((candidate, index) => (
                    <React.Fragment key={candidate.email || `low-${index}`}>
                      {/* data-application-id lets the Copilot "Locate" action scroll to this card */}
                      <div data-application-id={candidate.application_id ?? candidate.id} className="h-full">
                      <CandidateCard
                        candidate={{
                          ...candidate,
                          job_id: jobId,
                          is_profile_opened: Boolean(candidate.is_profile_opened || !isFreshProfile(candidate)),
                        }}
                        onViewProfile={() => handleViewProfile({...candidate, job_id: jobId})}
                        onViewResume={() => void persistProfileOpened({...candidate, job_id: jobId})}
                        jobDetails={jobDetails}
                        viewMode={viewMode}
                        videoStatus={videoStatuses[(candidate.candidate_email || candidate.email || '').toLowerCase()]}
                        nextRoundCandidates={shortlistedMeetingCandidates}
                        evaluating={evaluating}
                        handleEvaluate={handleEvaluate}
                        costPerResume={costPerResume}
                        onOpenCheatingEvidence={openInlineEvidence}
                      />
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

        </TabsContent>

        <TabsContent value="recommended" className="mt-4">
          {jobId ? <RecommendedCandidates jobId={jobId} /> : null}
        </TabsContent>

        <TabsContent value="trends" className="mt-4">
          {jobId ? <MarketTrends jobId={jobId} /> : null}
        </TabsContent>
      </Tabs>

      {/* Employer profile popup */}
      {selectedCandidate && (
        <EmployerProfilePopup
          profileData={{data: selectedCandidate}}
          onClose={handleCloseProfile}
        />
      )}

    </Stack>
  );
}

export default ApplicationList;
